import { match } from 'ts-pattern';
import { z, type ZodType, type ZodError } from 'zod';
import type { SafeParseReturnType } from 'zod';

type Ok<T> = { status: 'ok'; data: T };
type Failure<E> = { status: 'error'; error: E };
export type Result<T, E> = Ok<T> | Failure<E>;

export function ok<T, E>(data: T): Result<T, E> {
    return { status: 'ok', data };
}

export function fail<T, E>(error: E): Result<T, E> {
    return { status: 'error', error };
}

export function fromSafeZod<Input, Output>(
    parsed: SafeParseReturnType<Input, Output>,
): Result<Output, ZodError<Input>> {
    if (parsed.success) {
        return ok(parsed.data);
    } else {
        return fail(parsed.error);
    }
}

export function isFailure<T, E>(res: Result<T, E>): res is Failure<E> {
    return match(res)
        .with({ status: 'error' }, () => true)
        .otherwise(() => false);
}

export function isOk<T, E>(res: Result<T, E>): res is Ok<T> {
    return match(res)
        .with({ status: 'ok' }, () => true)
        .otherwise(() => false);
}

export function tap<T, E>(
    res: Result<T, E>,
    okCb: (data: T) => void,
    errorCb: (error: E) => void,
): void {
    match(res)
        .with({ status: 'ok' }, (res) => okCb(res.data))
        .with({ status: 'error' }, (res) => errorCb(res.error))
        .exhaustive();
}

export function map<T, E, N_T, N_E>(
    res: Result<T, E>,
    okCb: (data: T) => Result<N_T, E | N_E>,
    errorCb: (error: E) => Result<N_T, E | N_E>,
) {
    return match(res)
        .with({ status: 'ok' }, (res) => okCb(res.data))
        .with({ status: 'error' }, (res) => errorCb(res.error))
        .exhaustive();
}

export function mapOk<T, E, N_T, N_E>(
    res: Result<T, E>,
    callback: (data: T) => Result<N_T, E | N_E>,
): Result<N_T, E | N_E> {
    return match(res)
        .with({ status: 'ok' }, (res) => callback(res.data))
        .with({ status: 'error' }, (res) => res)
        .exhaustive();
}

export async function asyncMapOk<T, E, N_T, N_E>(
    res: Result<T, E>,
    callback: (data: T) => Promise<Result<N_T, E | N_E>>,
): Promise<Result<N_T, E | N_E>> {
    return match(res)
        .with({ status: 'ok' }, (res) => callback(res.data))
        .with({ status: 'error' }, (res) => Promise.resolve(res))
        .exhaustive();
}

export function mapError<T, E, N_E>(
    res: Result<T, E>,
    callback: (error: E) => Result<T, E | N_E>,
): Result<T, E | N_E> {
    return match(res)
        .with({ status: 'ok' }, (res) => res)
        .with({ status: 'error' }, (res) => callback(res.error))
        .exhaustive();
}

export function getWithDefault<T, E, D = T>(res: Result<T, E>, def: D): T | D {
    return match(res)
        .with({ status: 'ok' }, (res) => res.data)
        .with({ status: 'error' }, (_res) => def)
        .exhaustive();
}

export function getOrThrow<T, E>(res: Result<T, E>): T {
    return match(res)
        .with({ status: 'ok' }, (res) => res.data)
        .with({ status: 'error' }, (res) => {
            throw res.error;
        })
        .exhaustive();
}

export function resultZod<T, E>(dataType: ZodType<T>, errorType: ZodType<E>) {
    return z.discriminatedUnion('status', [
        z.object({
            status: z.literal('ok'),
            data: dataType,
        }),
        z.object({
            status: z.literal('error'),
            error: errorType,
        }),
    ]);
}
